import { BuildPresentationProps } from '../utils/build-presentation';
import { Slide } from '@features/powerpoint';
import { sectionTitleSlide } from '@features/lci-report/slides/shared/section-title-slide';
import { howWeDoItSlide } from '@features/lci-report/slides/appendix/how-we-do-it-slide';
import { lciAdaptiveSyntheticControlSlide } from '@features/lci-report/slides/appendix/lci-adaptive-synthetic-control/slide';
import { multiTouchAttributionSlide } from '@features/lci-report/slides/appendix/multi-touch-attribution/slide';
import { projectedVisitsMethodologySlide } from '@features/lci-report/slides/appendix/projected-visits-methodology/slide';
import { lciGuidelineMinimumsSlides } from '@features/lci-report/slides/appendix/lci-guideline-minimums/slide';
import { glossarySlides } from '@features/lci-report/slides/appendix/glossary/slides';
import { calculationsSlides } from '@features/lci-report/slides/appendix/calculations/slides';
import { emailSlide } from '@features/lci-report/slides/appendix/email/slide';
import { talkToUsSlide } from '@features/lci-report/slides/appendix/talk-to-us/slide';
import { salesAttributionTableSlides } from './appendix/dimension-tables/slides/sales-attribution-table';
import { visitAttributionTableSlides } from '@features/lci-report/subdomains/visitation/slides/appendix/dimension-tables/slides/visit-attribution-table';
import { GLOSSARY, CALCULATIONS } from '@features/glossary/subdomains/rsl';
import {
  GLOSSARY as VISITATION_GLOSSARY,
  CALCULATIONS as VISITATION_CALCULATIONS,
} from '@features/glossary/subdomains/visitation';

export const appendixSlidesFromProps = (
  props: BuildPresentationProps
): Slide[] => {
  if (!props.customFields.includedSlides.appendix) return [];

  const hasVisits = props.report.hasVisitMetrics;

  const glossary = hasVisits
    ? { ...VISITATION_GLOSSARY, ...GLOSSARY }
    : GLOSSARY;

  const calculations = hasVisits
    ? {
        ...CALCULATIONS,
        visitAttribution: VISITATION_CALCULATIONS.visitAttribution,
        projectedVisits: VISITATION_CALCULATIONS.projectedVisits,
      }
    : CALCULATIONS;

  return [
    sectionTitleSlide('Appendix'),
    ...(hasVisits
      ? visitAttributionTableSlides(
          props.visitMetrics,
          props.dimensionTableData,
          props.customFields,
          props.calculatedFields
        )
      : []),
    ...(props.salesSummary
      ? salesAttributionTableSlides({
          overallSalesMetrics: props.salesSummary,
          dimensionTableData: props.dimensionTableData,
          overallReport: props.report,
          customFields: props.customFields,
        })
      : []),
    ...glossarySlides({
      entriesPerColumn: hasVisits ? [9, 9, 8, 8] : [8, 8, 8, 4],
      glossary,
    }),
    ...calculationsSlides(calculations, {
      entriesPerColumnPerPage: hasVisits ? [5, 4, 3] : [5],
      totalPages: 2,
    }),
    howWeDoItSlide({
      toplineText:
        '<lci>Lift Conversion Index (LCI®)</lci>, powered by industry-leading, robust consumer visit and sales data, ' +
        'provides the most accurate and holistic view into how your media drives consumer visits and sales!',
      conversionText: 'Audience visits a store and/or makes a purchase',
      attributionText:
        'LCI measures incremental lift in visits and in-store + online sales!',
    }),
    lciAdaptiveSyntheticControlSlide({
      firstStepText:
        'Algorithm is seeded with historic visitation data, with ML modeled weighting within brand and category.',
    }),
    multiTouchAttributionSlide({
      title: 'LCI 3.0 Multi Touch Attribution (MTA)',
      metricType: 'sales',
    }),
    projectedVisitsMethodologySlide(),
    ...lciGuidelineMinimumsSlides(),
    talkToUsSlide(),
    emailSlide(),
  ];
};
